import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Loader2, LogOut, QrCode } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BlogCoversTab } from "./BlogCoversTab";
import { CasesTab } from "./CasesTab";
import { VideoTab } from "./VideoTab";
import { NewsletterTab } from "./NewsletterTab";
import { Logo } from "@/components/landing/Logo";
import { adminApi } from "./adminApi";
import { AdminLogin } from "./AdminLogin";
import { BookingsTab } from "./BookingsTab";
import { CalendarTab } from "./CalendarTab";
import { StaffTab } from "./StaffTab";
import { QuotesTab } from "./QuotesTab";

const Header = ({ user, onLogout }) => (
  <header className="sticky top-0 z-30 bg-[#F4F3EC]/85 backdrop-blur-xl border-b border-[#1B3B28]/10">
    <div className="mx-auto max-w-[1400px] px-4 sm:px-8 py-4 flex items-center justify-between gap-4">
      <a href="/" data-testid="admin-logo-link"><Logo /></a>
      <div className="flex items-center gap-3">
        {user.picture && <img src={user.picture} alt="" className="w-9 h-9 rounded-full hidden sm:block" />}
        <div className="hidden sm:block text-right leading-tight">
          <p className="text-sm text-[#1B3B28]" data-testid="admin-user-name">{user.name}</p>
          <p className="text-xs text-[#526356]">{user.role === "owner" ? "Titolare" : "Staff"}</p>
        </div>
        <a href="/volantino" target="_blank" rel="noopener noreferrer" data-testid="admin-flyer-link" className="flex items-center gap-2 rounded-full border border-[#1B3B28]/15 px-4 py-2 text-sm text-[#1B3B28] hover:bg-[#E6ECE4] transition-colors">
          <QrCode size={15} /> <span className="hidden sm:inline">Volantino QR</span>
        </a>
        <button onClick={onLogout} data-testid="admin-logout-btn" className="flex items-center gap-2 rounded-full border border-[#1B3B28]/15 px-4 py-2 text-sm text-[#1B3B28] hover:bg-[#E6ECE4] transition-colors">
          <LogOut size={15} /> Esci
        </button>
      </div>
    </div>
  </header>
);

const tabCls = "rounded-full px-5 py-2 text-sm data-[state=active]:bg-[#1B3B28] data-[state=active]:text-[#FCFBF7] data-[state=active]:shadow-none text-[#526356]";

export default function AdminPage() {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [authed, setAuthed] = useState(location.state?.user ? true : null);

  useEffect(() => {
    if (location.state?.user) return;
    adminApi.get("/auth/me").then(({ data }) => { setUser(data); setAuthed(true); }).catch(() => setAuthed(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = async () => { await adminApi.post("/auth/logout"); setUser(null); setAuthed(false); };

  if (authed === null) return <div className="min-h-screen grid place-items-center bg-[#F4F3EC]"><Loader2 className="animate-spin text-[#3E6B48]" /></div>;
  if (!authed) return <AdminLogin />;

  return (
    <div className="min-h-screen bg-[#F4F3EC]" data-testid="admin-dashboard">
      <Header user={user} onLogout={logout} />
      <main className="mx-auto max-w-[1400px] px-4 sm:px-8 py-10">
        <p className="eyebrow text-[#3E6B48]">Area riservata</p>
        <h1 className="f-serif text-4xl sm:text-5xl text-[#1B3B28] mt-2">Buongiorno, {user.name?.split(" ")[0] || "Dottore"}.</h1>
        <Tabs defaultValue="prenotazioni" className="mt-8">
          <TabsList className="bg-[#E6ECE4] rounded-full p-1 h-auto max-w-full overflow-x-auto justify-start" data-testid="admin-tabs">
            <TabsTrigger value="prenotazioni" className={tabCls} data-testid="admin-tab-bookings">Prenotazioni</TabsTrigger>
            <TabsTrigger value="calendario" className={tabCls} data-testid="admin-tab-calendar">Calendario</TabsTrigger>
            <TabsTrigger value="preventivi" className={tabCls} data-testid="admin-tab-quotes">Preventivi</TabsTrigger>
            <TabsTrigger value="casi" className={tabCls} data-testid="admin-tab-cases">Casi</TabsTrigger>
            <TabsTrigger value="blog" className={tabCls} data-testid="admin-tab-blog">Blog</TabsTrigger>
            <TabsTrigger value="video" className={tabCls} data-testid="admin-tab-video">Video</TabsTrigger>
            <TabsTrigger value="newsletter" className={tabCls} data-testid="admin-tab-newsletter">Newsletter</TabsTrigger>
            <TabsTrigger value="staff" className={tabCls} data-testid="admin-tab-staff">Staff</TabsTrigger>
          </TabsList>
          <TabsContent value="prenotazioni" className="mt-8"><BookingsTab /></TabsContent>
          <TabsContent value="calendario" className="mt-8"><CalendarTab /></TabsContent>
          <TabsContent value="preventivi" className="mt-8"><QuotesTab /></TabsContent>
          <TabsContent value="casi" className="mt-8"><CasesTab /></TabsContent>
          <TabsContent value="blog" className="mt-8"><BlogCoversTab /></TabsContent>
          <TabsContent value="video" className="mt-8"><VideoTab /></TabsContent>
          <TabsContent value="newsletter" className="mt-8"><NewsletterTab /></TabsContent>
          <TabsContent value="staff" className="mt-8"><StaffTab user={user} /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
